import React, { Component } from "react";
import { Button, Icon, Modal, Grid, Segment, Header, Popup, Label } from "semantic-ui-react";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import Select from "components/Form/Select";
import { searchPatientsMyLab } from "services/patient";
import { displayGenderText } from "utils/displayGender";
import debounce from "lodash.debounce";
import moment from "moment";
import FilePreview from "../FilePreview";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import CustomInputTextWithAction from "components/Form/CustomInputTextWithAction";
import CustomDatePickerComponent from "components/Form/CustomDatePickerComponent";
import RadioButtons from "components/Form/RadioButtons";
import InputPhone from "components/Form/InputPhone";
import PatientData from "components/Others/PatientData";
import { convertPatientToMyLab } from "utils/patientConverter";
import { orderedSpecialRegimes } from "utils/selectOptions";
import { isValidPhoneNumber, formatPhoneNumber } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { cleanPhoneNumber } from "utils/phone";
import * as cdigit from "cdigit";
import SearchPatient from "../../../../components/SearchPatient/index";

const styles = {
	descriptionContainer: {
		display: "flex",
		justifyContent: "center",
	},
	descriptionTitle: {
		display: "flex",
		justifyContent: "flex-end",
		padding: "0 10px",
		flex: 1,
		alignItems: "flex-start",
	},
	description: {
		display: "flex",
		justifyContent: "flex-start",
		flexDirection: "column",
		alignItems: "flex-start",
		flex: 1,
	},
};
const schemaValidation = Yup.object().shape({
	birth_name: Yup.string().required("Champ requis"),
	first_name: Yup.string().required("Champ requis"),
	birth_date: Yup.date().required("Champ requis"),
	gender: Yup.string().required("Champ requis"),

	cns: Yup.string().when("ignoreCnsError", {
		is: false,
		then: Yup.string()
			.required("CNS requis")
			.test(
				"compareWithBirthDate",
				"Le numéro CNS et la date de naissance ne sont pas similaires",
				//Yup ref doesn't work with functional arrows
				function (value) {
					if (!value || value === undefined) return false;
					if (value?.length === 8 || value?.length === 13) {
						const ref = Yup.ref("birth_date");
						const birthDate = moment(this.resolve(ref)).format("YYYYMMDD");
						const birthDate2000 = moment(this.resolve(ref)).add(2000, "year").format("YYYYMMDD");
						const cnsBirthDate = value.substring(0, 8);
						return birthDate === cnsBirthDate || birthDate2000 === cnsBirthDate;
					}
					return true;
				}
			)
			.test("cnsLength", "Le numéro CNS doit être composé de 8 ou 13 chiffres", (value) => {
				if (!value || value === undefined) return false;
				return value.match(new RegExp("^(([0-9]{13})|([0-9]{8}))"));
			})
			.test("cnsValidation", "Numéro CNS invalide", (value) => {
				if (!value || value === undefined) return false;

				if (value?.length === 8) return value.match(new RegExp(process.env.REACT_APP_NO_CNS_REGEX));

				if (value?.length === 13) {
					const stringToValidate = value.substring(0, 11);
					const luhnVal = value.charAt(11);
					const verhoeffVal = value.charAt(12);
					return (
						value.match(new RegExp(process.env.REACT_APP_CNS_REGEX)) &&
						cdigit.luhn.compute(stringToValidate) === luhnVal &&
						cdigit.verhoeff.compute(stringToValidate) === verhoeffVal
					);
				}

				return false;
			}),
	}),
	email: Yup.string().email("Email incorrect"),
	mobile_phone: Yup.string()
		.min(2, "Longueur min : 2 caractères")
		.max(50, "Longueur max : 50 caractères")
		.test("phoneTypeValidation", "Il ne s'agit pas d'un numéro de téléphone mobile", (value) => {
			if (!value || value === "+352") return true;
			if (formatPhoneNumber(value) !== "") {
				const type = parsePhoneNumberFromString(value).getType();
				return type === "MOBILE" || type === "FIXED_LINE_OR_MOBILE";
			}
		})
		.test("phoneValidation", "Le format du téléphone mobile est inconnu", (value) => {
			if (!value || value === "+352") return true;
			return isValidPhoneNumber(cleanPhoneNumber(value));
		}),
	specialRegime: Yup.object().shape({
		id: Yup.string().required("Régime requis"),
	}),
});

class ManagePatientExpressModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			patientLoading: false,
			patientSelected: null,
			newPatient: false,
		};
	}

	filterPatients = debounce((searchTerm, callback) => {
		const { generalParameters } = this.props;
		this.props
			.searchPatientsMyLab({ multiple_search: searchTerm }, 20, 0)
			.then(() => {
				this.setState({ patientLoading: false });
				callback(
					this.props.patients.search?.list?.map((patient) => {
						return {
							id: patient.id,
							label: (patient.active ? "MyLab activé " : "").concat(
								patient.cns || patient.matricule || "",
								patient.gender ? ` ${displayGenderText(patient, generalParameters.data)}` : "",
								`${patient.last_name || ""}
                                ${(patient.birth_name && patient.birth_name !== patient.last_name && patient.birth_name) || ""} ${
									patient.first_name || ""
								}`,
								patient.birth_name || patient.birth_date ? ` né${patient.gender === "f" ? "e" : ""}  ` : "",
								patient.birth_date
									? ` le ${moment(patient.birth_date).format(process.env.REACT_APP_DATE_DISPLAY_FORMAT)} - ${moment().diff(
											patient.birth_date,
											"years"
									  )} ans`
									: "",
								` ${patient.mobile_phone || ""}`,
								` ${patient.email || ""}`
							),
							...patient,
						};
					})
				);
			})
			.catch((error) => {
				this.setState({ patientLoading: false });
				callback(error);
			});
	}, 500);

	updatePatientData = (setFieldValue) => {
		const { mpiPatient } = this.props;
		setFieldValue("birth_name", mpiPatient.birth_name?.toUpperCase());
		setFieldValue("first_name", mpiPatient.first_name);
		setFieldValue("married_name", mpiPatient.married_name?.toUpperCase());
		setFieldValue("birth_date", mpiPatient.birth_date);
		setFieldValue("gender", mpiPatient?.gender?.toLowerCase() === "m" ? "male" : mpiPatient?.gender?.toLowerCase() === "f" ? "female" : "");
		setFieldValue("cns", mpiPatient.matricule);
		setFieldValue("mobile_phone", mpiPatient.mobile_phone);
		setFieldValue("email", mpiPatient.email);
		if (mpiPatient.address) {
			setFieldValue("home_street", mpiPatient.address.street);
			setFieldValue("home_country", mpiPatient.address.country);
			setFieldValue("home_city", mpiPatient.address.city);
			setFieldValue("home_postal_code", mpiPatient.address.postal_code);
		}
	};

	render() {
		const { open, close, submit, files, specialRegimes, searchByMatricule, mpiPatient, initMpiPatient } = this.props;
		const { patientSelected, newPatient } = this.state;
		const withFiles = files && files.length > 0;

		return (
			<Formik
				enableReinitialize
				schemaValidation={schemaValidation}
				initialValues={{
					mylabId: 0,
					birth_name: "",
					first_name: "",
					married_name: "",
					birth_date: "",
					gender: "",
					cns: "",
					mobile_phone: "",
					email: "",
					patient: { id: 0 },
					home_number: "",
					home_complement: "",
					home_street: "",
					home_country: "",
					home_city: "",
					home_postal_code: "",
					specialRegime: { id: "" },
				}}
				onSubmit={(values, { setSubmitting }) => {
					let patient = convertPatientToMyLab(values);
					submit(patient).then(() => {
						setSubmitting(false);
					});
					close();
				}}
			>
				{({ submitForm, setFieldValue, values, errors, touched }) => (
					<Form>
						<Modal size={withFiles ? "fullscreen" : "small"} open={open} closeOnEscape closeOnDimmerClick onClose={close}>
							<Modal.Header>Sélectionner un patient</Modal.Header>
							<Modal.Content>
								<Grid>
									<Grid.Row>
										{withFiles && (
											<Grid.Column width={9} style={{ height: "78vh" }}>
												<FilePreview files={files} isModal={true} />
											</Grid.Column>
										)}
										<Grid.Column width={withFiles ? 7 : 16}>
											<SearchPatient
												isSelectSearch
												selectCallback={(patient) => {
													initMpiPatient();
													this.setState({
														patientSelected: patient,
														newPatient: false,
													});
												}}
												addNewPatientCallback={() => {
													initMpiPatient();
													this.setState({ newPatient: true, patientSelected: null });
												}}
											/>

											{this.state.patientSelected && !this.state.newPatient && (
												<>
													<Header as="h3" attached="top">
														<div>
															<span>Patient sélectionné</span>
															<Label style={{ float: "right", cursor: "pointer", marginRight: 0, width: "28px" }}>
																<Icon name="times" onClick={() => this.setState({ patientSelected: null })} />
															</Label>
														</div>
													</Header>

													<Segment attached>
														<PatientData patient={patientSelected} />
													</Segment>
												</>
											)}
											{this.state.newPatient && !this.state.patientSelected && (
												<>
													<Header as="h3" attached="top">
														<div>
															<span>Patient à créer</span>
															<Label style={{ float: "right", cursor: "pointer", marginRight: 0, width: "28px" }}>
																<Icon
																	name="times"
																	onClick={() => {
																		this.setState({ newPatient: false });
																		initMpiPatient();
																	}}
																/>
															</Label>
														</div>
													</Header>
													<Segment attached>
														<Grid>
															<Grid.Row>
																<Grid.Column mobile={16} tablet={16} computer={16}>
																	<Field type="number" name="mylabId" hidden />
																	<Field name="cns">
																		{({ field, form }) => (
																			<div>
																				<CustomInputTextWithAction
																					field={field}
																					form={form}
																					customlabel="N° CNS"
																					required
																					placeholder=""
																					iconName="search"
																					iconDisabled={!form.values.cns || form.values.cns.toString().length !== 13}
																					onIconClick={() => searchByMatricule(form.values.cns)}
																					onKeyUp={(e) => {
																						const val = e.currentTarget.value;
																						setTimeout(() => {
																							if (val?.length === 8 || val?.length === 13) {
																								let birth_date = moment(val.substring(0, 8), "YYYYMMDD", "YYYY-MM-DD");
																								if (birth_date.year() > moment().year()) birth_date = birth_date.add(-2000, "year");
																								if (birth_date.isValid()) form.setFieldValue("birth_date", birth_date.format("YYYY-MM-DD"));
																							}
																						}, 500);
																					}}
																				/>
																			</div>
																		)}
																	</Field>
																</Grid.Column>
																{mpiPatient && (
																	<Grid.Column
																		mobile={16}
																		tablet={16}
																		computer={16}
																		style={{
																			textAlign: "center",
																			borderRadius: "0px 0px 20px 20px",
																			border: "4px solid mediumseagreen",
																			padding: 14,
																			margin: "0 14px",
																		}}
																	>
																		<div>
																			<div style={{ textAlign: "right", fontWeight: "bold", marginBottom: 5 }}>
																				<Icon name="times" onClick={() => initMpiPatient()} style={{ cursor: "pointer" }} />
																			</div>
																			<div style={{ textAlign: "left", fontWeight: "bold", marginBottom: 5 }}>
																				Patient trouvé dans l'annuaire national
																			</div>
																		</div>
																		<div style={{ textAlign: "left" }}>Voulez-vous remplir la fiche patient avec ces données ?</div>
																		<Segment attached>
																			<div style={styles.descriptionContainer}>
																				<span style={styles.descriptionTitle}>Nom de naissance :</span>
																				<span style={styles.description}>{mpiPatient.birth_name?.toUpperCase()}</span>
																			</div>
																			<div style={styles.descriptionContainer}>
																				<span style={styles.descriptionTitle}>Prénom(s) :</span>
																				<span style={styles.description}>{mpiPatient.first_name}</span>
																			</div>
																			<div style={styles.descriptionContainer}>
																				<span style={styles.descriptionTitle}>Genre : </span>
																				<span style={styles.description}>{mpiPatient.gender?.toLowerCase() === "m" ? "Homme" : "Femme"}</span>
																			</div>
																			{mpiPatient.marital_name && (
																				<div style={styles.descriptionContainer}>
																					<span style={styles.descriptionTitle}>Nom marital : </span>
																					<span style={styles.description}>{mpiPatient.marital_name}</span>
																				</div>
																			)}
																			<div style={styles.descriptionContainer}>
																				<span style={styles.descriptionTitle}>Date de naissance : </span>
																				<span style={styles.description}>
																					{mpiPatient.birth_date
																						? moment(mpiPatient.birth_date).format(process.env.REACT_APP_DATE_DISPLAY_FORMAT)
																						: "/"}
																				</span>
																			</div>
																			<div style={styles.descriptionContainer}>
																				<span style={styles.descriptionTitle}>Matricule :</span>
																				<span style={styles.description}>{mpiPatient.matricule}</span>
																			</div>
																			{mpiPatient.phone && (
																				<div style={styles.descriptionContainer}>
																					<span style={styles.descriptionTitle}>GSM: </span>
																					<span style={styles.description}>{mpiPatient.phone}</span>
																				</div>
																			)}
																			{mpiPatient.email && (
																				<div style={styles.descriptionContainer}>
																					<span style={styles.descriptionTitle}>Adresse email: </span>
																					<span style={styles.description}>{mpiPatient.email}</span>
																				</div>
																			)}
																			{mpiPatient?.address && (
																				<div style={styles.descriptionContainer}>
																					<span style={styles.descriptionTitle}>Adresse: </span>
																					<span style={styles.description}>
																						{mpiPatient.address?.street && (
																							<div style={{ textAlign: "left" }}>
																								{mpiPatient.address?.street} <br />
																							</div>
																						)}
																						{mpiPatient.address?.complement && (
																							<div style={{ textAlign: "left" }}>
																								{mpiPatient.address?.complement} <br />
																							</div>
																						)}

																						<div style={{ textAlign: "left" }}>{`${mpiPatient.address.postal_code} 
                                                                                        ${mpiPatient.address.city} ${mpiPatient.address.country}`}</div>
																					</span>
																				</div>
																			)}
																		</Segment>
																		<Button
																			color="green"
																			type="button"
																			style={{ marginTop: 15, float: "right" }}
																			onClick={(e) => {
																				if (e !== undefined) e.preventDefault();
																				this.updatePatientData(setFieldValue);
																				initMpiPatient();
																			}}
																		>
																			{" "}
																			<Icon name="long arrow alternate down" /> Utiliser ces données
																		</Button>
																	</Grid.Column>
																)}
															</Grid.Row>
														</Grid>
														<Field
															component={Select}
															name="specialRegime.id"
															customlabel={"Régime"}
															placeholder="Sélectionner un régime"
															options={orderedSpecialRegimes(specialRegimes.list)}
															required
														/>

														<Field
															type="text"
															name="birth_name"
															customlabel="Nom de naissance"
															placeholder=""
															component={CustomInputTextComponent}
															required
															forceUpperCase
															errors={errors}
															disabled={patientSelected}
														/>

														<Field
															type="text"
															name="first_name"
															customlabel="Prénom(s)"
															placeholder=""
															component={CustomInputTextComponent}
															required
															forceCapitalize
															errors={errors}
															disabled={patientSelected}
														/>

														<Field
															type="text"
															name="married_name"
															customlabel="Nom marital"
															placeholder=""
															component={CustomInputTextComponent}
															forceUpperCase
															disabled={patientSelected}
														/>

														<Field
															name="birth_date"
															customlabel="Date de naissance"
															component={CustomDatePickerComponent}
															disabled={patientSelected}
															required
														/>

														<RadioButtons
															name="gender"
															field1={{ id: "male", customlabel: "Homme" }}
															field2={{ id: "female", customlabel: "Femme" }}
															customlabel="Sexe"
															disabled={patientSelected}
															errors={errors}
															touched={touched}
															required
														/>

														<Field
															type="text"
															name="email"
															placeholder=""
															customlabel="Adresse email"
															component={CustomInputTextComponent}
															disabled={patientSelected}
														/>
														<Field
															component={InputPhone}
															name="mobile_phone"
															placeholder=""
															customlabel="Téléphone mobile"
															disabled={patientSelected}
														/>
													</Segment>
												</>
											)}
										</Grid.Column>
									</Grid.Row>
								</Grid>
							</Modal.Content>
							<Modal.Actions>
								<Button
									onClick={(e) => {
										if (e !== undefined) e.preventDefault();
										this.setState({ patientSelected: null, newPatient: false });
										close();
									}}
								>
									Annuler
								</Button>
								<Button
									primary
									onClick={(e) => {
										if (e !== undefined) e.preventDefault();
										if (patientSelected) submit(patientSelected);
										else {
											console.log("appel submitform", values);

											submitForm(values);
										}
										this.setState({ patientSelected: null, newPatient: false });
										close();
									}}
									disabled={Object.entries(errors).length > 0 && errors.constructor === Object}
								>
									Confirmer
								</Button>
							</Modal.Actions>
						</Modal>
					</Form>
				)}
			</Formik>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		patients: state.patients,
		generalParameters: state.generalParameters,
		specialRegimes: state.specialRegimes,
	};
};

const mapDispatchToProps = {
	searchPatientsMyLab,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManagePatientExpressModal);
